/* eslint-disable */

// This file was automatically generated by @icp-sdk/bindgen@0.4.1.
// You should NOT make any changes in this file as it will be overwritten.
// Additionally, you should also exclude this file from your linter and/or formatter to prevent it from being checked or modified.

import type { ActorMethod } from '@icp-sdk/core/agent';
import type { Principal } from '@icp-sdk/core/principal';
import { IDL } from '@icp-sdk/core/candid';

export interface BaseConfig {
  'token' : BaseToken,
  /**
   * XRC symbol for USD pricing ("ICP", "BTC", "ETH"). None (with no `price_pool`) means pegged to $1.
   */
  'xrc_symbol' : [] | [string],
  /**
   * For the cycles ledger (TCYCLES): priced at what the cycles minting canister charges, 1T cycles
   * for its ICP rate times ICP's USD price. Not a market price, so nobody can move it by trading.
   */
  'price_cmc' : [] | [boolean],
  /**
   * For a token the exchange rate canister doesn't price (BOB): its ICPSwap pool against ICP. The
   * USD price is that pool's price times ICP's, taken as the median of recent half-hourly readings
   * so a single manipulated moment can't move it. Optional so configs saved before it still load.
   */
  'price_pool' : [] | [Principal],
}
export interface BaseToken {
  'fee' : bigint,
  'decimals' : number,
  /**
   * How ICPSwap names this ledger's standard: "ICP" or "ICRC2".
   */
  'icpswap_standard' : string,
  'ledger' : Principal,
  'symbol' : string,
}
export interface Bucket {
  'cycles_burned' : bigint,
  'launches' : bigint,
  'hour' : bigint,
  'trades' : bigint,
  'rigs' : bigint,
  'volume' : Array<[Principal, bigint]>,
  'graduations' : bigint,
  'fees_platform' : Array<[Principal, bigint]>,
  'blocks' : bigint,
  'cycles_minted' : bigint,
  'fees_creator' : Array<[Principal, bigint]>,
}
export interface Config {
  'cmc' : [] | [Principal],
  'xrc' : [] | [Principal],
  'mine_supply' : bigint,
  'lp_supply' : bigint,
  'rig_tick_secs' : bigint,
  'rig_hashes_per_power' : bigint,
  'halving_blocks' : bigint,
  'block_secs' : bigint,
  'fee_bps' : number,
  'icpswap' : IcpSwapIds,
  /**
   * The least TCYCLES (in cycles) a new rig takes with spawn_rig_with_cycles. Optional (like
   * `cycles_ledger`) so a newer website still reads an older factory's config.
   */
  'rig_min_cycles' : [] | [bigint],
  'icp_ledger' : Principal,
  'virtual_base_usd' : number,
  'child_controllers' : Array<Principal>,
  'rig_min_e8s' : bigint,
  'factory' : Principal,
  'bases' : Array<BaseConfig>,
  'curve_supply' : bigint,
  'ledger_wasm_sha256' : string,
  'total_supply' : bigint,
  /**
   * The Cycles Ledger rigs can be fuelled from, once TCYCLES is a base.
   */
  'cycles_ledger' : [] | [Principal],
  'treasury' : Principal,
}
export interface Event {
  'at' : bigint,
  'who' : Principal,
  'kind' : EventKind,
  'market' : Principal,
}
export type EventKind = { 'Buy' : { 'base' : bigint, 'tokens' : bigint } } |
  { 'Launched' : null } |
  { 'Sell' : { 'base' : bigint, 'tokens' : bigint } } |
  { 'Block' : { 'height' : bigint, 'reward' : bigint } } |
  { 'Graduated' : { 'pool' : Principal } };
export interface EventView {
  'at' : bigint,
  'who' : Principal,
  'base' : [] | [Principal],
  'kind' : EventKind,
  'name' : string,
  'market' : Principal,
  'symbol' : string,
}
export interface FactoryInit {
  /**
   * None only for local development: canisters are then paid from the factory's own cycles.
   */
  'cmc' : [] | [Principal],
  'xrc' : [] | [Principal],
  /**
   * Fund above this is swept to the treasury.
   */
  'graduation_fund_cap_e8s' : bigint,
  /**
   * ICP kept from each launch fee towards ICPSwap's 1 ICP pool fee.
   */
  'graduation_reserve_e8s' : bigint,
  'icpswap' : IcpSwapIds,
  /**
   * Cycles given to each new market and ledger.
   */
  'market_cycles' : bigint,
  'icp_ledger' : Principal,
  /**
   * Virtual base reserve in USD. Graduation raises 3x this.
   */
  'virtual_base_usd' : number,
  /**
   * Final controllers of every canister created. Mainnet: [blackhole]. Empty: no controller at all.
   */
  'child_controllers' : Array<Principal>,
  /**
   * Minimum ICP to spawn a rig.
   */
  'rig_min_e8s' : bigint,
  'bases' : Array<BaseConfig>,
  'ledger_cycles' : bigint,
  /**
   * Used only when `xrc` is None (local development).
   */
  'dev_usd_prices' : Array<[Principal, number]>,
  'status_proxy' : [] | [Principal],
  'treasury' : Principal,
}
export interface Health {
  'floor' : bigint,
  'last_error' : [] | [string],
  'last_refuel_e8s' : bigint,
  'factory_cycles' : bigint,
  'last_refuel_at' : bigint,
  'canisters' : Array<[Principal, bigint]>,
  /**
   * Listed tokens' ICPSwap pools: (market, pool, cycles) at the last hourly check.
   */
  'pools' : Array<[Principal, Principal, bigint]>,
  'markets_low' : number,
  'spendable_icp_e8s' : bigint,
  'keep_for_listings_e8s' : bigint,
  'last_run' : bigint,
  /**
   * ICP spent fuelling ICPSwap pools so far, in e8s.
   */
  'pool_fuel_e8s' : bigint,
}
export interface IcpSwapIds {
  'passcode_manager' : Principal,
  'factory' : Principal,
  'calculator' : Principal,
}
export interface Job {
  'id' : bigint,
  'x0' : bigint,
  'creator' : Principal,
  'args' : LaunchArgs,
  'fee_e8s' : bigint,
  'created_at' : bigint,
  'error' : [] | [string],
  'stage' : JobStage,
  /**
   * The creator's first buy (base units), held by the factory until the market is installed.
   */
  'dev_buy' : [] | [bigint],
  /**
   * Set once the first buy has reached the market, so a launch resumed later doesn't resend it.
   */
  'dev_sent' : [] | [boolean],
}
export type JobStage = {
    'MarketInstalled' : { 'ledger' : Principal, 'market' : Principal }
  } |
  { 'LedgerInstalled' : { 'ledger' : Principal, 'market' : Principal } } |
  { 'Done' : { 'ledger' : Principal, 'market' : Principal } } |
  { 'Paid' : null } |
  { 'BothCreated' : { 'ledger' : Principal, 'market' : Principal } } |
  { 'Funded' : null } |
  { 'MarketCreated' : { 'market' : Principal } };
export interface Launch {
  'mined' : bigint,
  'creator' : Principal,
  'base' : Principal,
  'trades' : bigint,
  'name' : string,
  /**
   * Marked NSFW by its creator at launch.
   */
  'nsfw' : [] | [boolean],
  'sold' : bigint,
  'description' : string,
  'created_at' : bigint,
  'price_x' : bigint,
  'price_y' : bigint,
  'ledger' : Principal,
  'reported_at' : bigint,
  'blocks' : bigint,
  'last_trade_at' : bigint,
  'graduated' : boolean,
  /**
   * Tokens the creator bought at launch (their first buy), if any. Public, so anyone can see it.
   */
  'dev_tokens' : [] | [bigint],
  'raised' : bigint,
  'progress_bps' : number,
  'market' : Principal,
  'active_rigs' : number,
  'icpswap_pool' : [] | [Principal],
  'symbol' : string,
  'volume_base' : bigint,
}
export interface LaunchArgs {
  'base' : Principal,
  'meta' : TokenMeta,
  /**
   * The creator marks the token as adult (NSFW) content. Soon's website then hides it unless a
   * viewer chooses to see NSFW tokens. Like pump.fun: allowed, but labelled.
   */
  'nsfw' : [] | [boolean],
  /**
   * Optional first buy by the creator, in base units (trading fee included). Approve the factory
   * on the base ledger for this plus one ledger fee. It is the curve's first trade, before anyone
   * else can buy, and it is shown publicly on the token.
   */
  'dev_buy' : [] | [bigint],
}
export interface LaunchQuote {
  'x0' : bigint,
  'usd_price' : number,
  'base' : BaseToken,
  'fee_e8s' : bigint,
  'graduation_raise' : bigint,
}
/**
 * What a market tells the factory every report interval. Deltas since the last report.
 */
export interface MarketReport {
  'mined' : bigint,
  'buys' : bigint,
  'trades' : bigint,
  'sold' : bigint,
  'price_x' : bigint,
  'price_y' : bigint,
  'cycles' : bigint,
  'cycles_burned_rigs' : bigint,
  'cycles_burned_market' : bigint,
  'events' : Array<Event>,
  'fees_platform' : bigint,
  'blocks' : bigint,
  'graduated' : boolean,
  'raised' : bigint,
  'active_rigs' : number,
  'icpswap_pool' : [] | [Principal],
  'volume_base' : bigint,
  'fees_creator' : bigint,
}
export interface MiningFeesView {
  'platform_total_e8s' : bigint,
  'creator_total_e8s' : bigint,
  /**
   * Fee on rig fuel, in basis points (100 = 1%). Half goes to the token's creator.
   */
  'fee_bps' : number,
  'creator_owed_e8s' : bigint,
  'creator_paid_e8s' : bigint,
}
export interface Page { 'total' : bigint, 'items' : Array<Launch> }
export type Result = { 'Ok' : null } |
  { 'Err' : string };
export type Result_1 = { 'Ok' : Principal } |
  { 'Err' : string };
export type Result_2 = { 'Ok' : Job } |
  { 'Err' : string };
export type Result_3 = { 'Ok' : bigint } |
  { 'Err' : string };
export type Result_4 = { 'Ok' : LaunchQuote } |
  { 'Err' : string };
export type Result_5 = { 'Ok' : bigint } |
  { 'Err' : string };
export type Sort = { 'Active' : null } |
  { 'Graduated' : null } |
  { 'Progress' : null } |
  { 'Miners' : null } |
  { 'Newest' : null } |
  { 'Raised' : null };
export interface SpawnRigArgs {
  /**
   * ICP to convert into the rig's cycles. Approve the factory for this plus the ledger fee.
   */
  'e8s' : bigint,
  'market' : [] | [Principal],
  'power' : number,
}
export interface SpawnRigWithCyclesArgs {
  /**
   * Cycles (TCYCLES units, 12 decimals) to spend from your Cycles Ledger account: 1% is the
   * mining fee, the rest fuels the rig. Approve the factory for this plus 2 ledger fees.
   */
  'cycles' : bigint,
  'market' : [] | [Principal],
  'power' : number,
}
export interface Stats {
  'now' : bigint,
  'factory_cycles' : bigint,
  'launches' : bigint,
  'trades' : bigint,
  'rigs' : bigint,
  'volume' : Array<[Principal, bigint]>,
  'icp_converted_e8s' : bigint,
  'graduation_fund_e8s' : bigint,
  'cycles_burned_rigs' : bigint,
  'graduations' : bigint,
  'cycles_spent_creating' : bigint,
  'fees_platform' : Array<[Principal, bigint]>,
  'cycles_burned_total' : bigint,
  'blocks' : bigint,
  'cycles_minted' : bigint,
  'cycles_burned_markets' : bigint,
  'cycles_burned_factory' : bigint,
  'fees_creator' : Array<[Principal, bigint]>,
}
export interface TcFeesView {
  'platform_paid_cycles' : bigint,
  'refunds_owed_cycles' : bigint,
  /**
   * What the factory must hold on the Cycles Ledger to pay everyone it owes.
   */
  'held_for_others_cycles' : bigint,
  /**
   * Same fee as ICP fuel, in basis points. Half goes to the token's creator, paid in TCYCLES.
   */
  'fee_bps' : number,
  /**
   * Fuellings waiting to be settled because the ledger's answer was unclear: (case, details).
   */
  'uncertain' : Array<[bigint, TcUncertain]>,
  'creator_owed_cycles' : bigint,
  'rig_min_cycles' : bigint,
  'top_up_min_cycles' : bigint,
  /**
   * Cycles delivered from users' TCYCLES to rigs, markets and ledgers.
   */
  'delivered_cycles' : bigint,
  'platform_total_cycles' : bigint,
  'creator_paid_cycles' : bigint,
  'creator_total_cycles' : bigint,
  'cycles_ledger' : [] | [Principal],
}
export interface TcUncertain {
  'to' : Principal,
  'fee' : bigint,
  'who' : Principal,
  'fuel' : bigint,
  'created_at' : bigint,
  'market' : [] | [Principal],
  'reason' : string,
}
export interface TokenMeta {
  'x' : [] | [string],
  /**
   * `data:image/...;base64,...`, at most 48 KiB.
   */
  'logo' : string,
  'name' : string,
  'description' : string,
  'website' : [] | [string],
  'telegram' : [] | [string],
  'symbol' : string,
}
export interface TokenReport {
  'at' : bigint,
  'id' : bigint,
  /**
   * How to reach the reporter, if they want a reply (an email or handle). Never shown publicly.
   */
  'contact' : [] | [string],
  'details' : string,
  'market' : Principal,
  'reporter' : Principal,
  /**
   * "impersonation", "trademark", "scam", "nsfw", "other".
   */
  'reason' : string,
}
export interface UsdPrice {
  'at' : bigint,
  'usd' : number,
  'ledger' : Principal,
  'symbol' : string,
}
export interface _SERVICE {
  /**
   * Beta: add a base token that new launches can be priced in. Controllers only. Everything is
   * checked on-chain first: the ledger's name, decimals, fee and ICRC-2 support must match the
   * config, and a price pool must be ICPSwap's own 0.3% pool for this token against ICP.
   */
  'add_base' : ActorMethod<[BaseConfig], Result>,
  /**
   * Hourly buckets in [from, to). Daily and monthly views are sums of these.
   */
  'buckets' : ActorMethod<[bigint, bigint], Array<Bucket>>,
  /**
   * Clear an abusive display name or rig name. Controllers only.
   */
  'clear_name' : ActorMethod<[Principal], Result>,
  /**
   * Every number and canister id an integrator or agent needs, in one call.
   */
  'config' : ActorMethod<[], Config>,
  /**
   * The latest activity across every market, newest first, with each token's ticker attached.
   */
  'events' : ActorMethod<[number], Array<EventView>>,
  /**
   * Called by a market whose curve has filled. Creates (or finds) its ICPSwap pool at the final curve price.
   */
  'graduate' : ActorMethod<[Principal], Result_1>,
  /**
   * Public: how well fuelled the platform is.
   */
  'health' : ActorMethod<[], Health>,
  /**
   * Tokens Soon's website doesn't feature, with the public reason and when. Public: the list is open.
   */
  'hidden' : ActorMethod<[], Array<[Principal, string, bigint]>>,
  'job' : ActorMethod<[bigint], [] | [Job]>,
  'jobs_of' : ActorMethod<[Principal], Array<Job>>,
  /**
   * Launch a token. The caller must first `icrc2_approve` the factory on the ICP ledger for the
   * fee from `quote_launch` plus the ledger fee.
   */
  'launch' : ActorMethod<[LaunchArgs], Result_2>,
  'launch_of' : ActorMethod<[Principal], [] | [Launch]>,
  'market_of_ledger' : ActorMethod<[Principal], [] | [Principal]>,
  'markets' : ActorMethod<[Sort, bigint, bigint, [] | [Principal]], Page>,
  /**
   * Tokens this principal has bought, sold, mined or launched, newest first. Wallets use it to know
   * which ledgers to ask for balances.
   */
  'markets_of' : ActorMethod<[Principal], Array<Launch>>,
  /**
   * A creator's mining fees waiting to be sent, in e8s.
   */
  'mining_fee_owed' : ActorMethod<[Principal], bigint>,
  /**
   * A creator's TCYCLES mining fees waiting to be sent, in cycles.
   */
  'mining_fee_owed_cycles' : ActorMethod<[Principal], bigint>,
  'mining_fees' : ActorMethod<[], MiningFeesView>,
  /**
   * Display names for up to 200 principals (those without one are left out).
   */
  'names' : ActorMethod<[Array<Principal>], Array<[Principal, string]>>,
  /**
   * Every NSFW token: marked by its creator at launch or by Soon. Public.
   */
  'nsfw' : ActorMethod<[], Array<Principal>>,
  /**
   * Send creators their mining fees. Runs daily; anyone may trigger it, and each share can only go
   * to its creator. Shares under 100 ledger fees wait so transfer fees don't eat them.
   */
  'pay_mining_fees' : ActorMethod<[], Result_3>,
  /**
   * Send creators and the treasury their TCYCLES mining fees, and any refunds still owed. Runs
   * daily; anyone may trigger it, and each share can only go to whom it is owed. Returns the
   * cycles sent.
   */
  'pay_tc_fees' : ActorMethod<[], Result_3>,
  /**
   * Latest USD price per base token (from the exchange rate canister; $1 for stablecoins).
   */
  'prices' : ActorMethod<[], Array<UsdPrice>>,
  /**
   * A query: it only reads cached prices, so nobody can make the factory pay for lookups by asking.
   */
  'quote_launch' : ActorMethod<[Principal], Result_4>,
  'report' : ActorMethod<[MarketReport], undefined>,
  /**
   * Report a token. Anyone may (signed in or not), within limits: one report a minute per caller and
   * a daily cap overall, so spam can't cost much.
   */
  'report_token' : ActorMethod<
    [Principal, string, string, [] | [string]],
    Result_5
  >,
  /**
   * Reports, newest first. Only the factory's controllers can read them (they may hold contact details).
   */
  'reports' : ActorMethod<[bigint, bigint], Array<TokenReport>>,
  /**
   * Who goes by each of up to 50 names (ignoring case); unknown names are left out. For @mentions.
   */
  'resolve_names' : ActorMethod<[Array<string>], Array<[string, Principal]>>,
  /**
   * Settle a fuelling whose outcome was unknown, after checking the Cycles Ledger's blocks (a
   * withdraw_from block followed by a refund block means it didn't arrive). Delivered: the fee is
   * booked, and a new rig's cycles become the owner's rig credit (spawn_rig with e8s = 0 uses it).
   * Not delivered: the fee goes back to the payer. Controllers only; it can only book the held fee
   * or return it to whoever paid it.
   */
  'resolve_tc_uncertain' : ActorMethod<[bigint, boolean], Result>,
  /**
   * Continue a launch that stopped part way (for example a subnet was briefly unavailable).
   */
  'resume_launch' : ActorMethod<[bigint], Result_2>,
  /**
   * Finish installing a rig whose creation was interrupted. Its cycles are already inside it.
   */
  'resume_rig' : ActorMethod<[Principal], Result_1>,
  /**
   * Names of up to 200 rigs (those without one are left out).
   */
  'rig_names' : ActorMethod<[Array<Principal>], Array<[Principal, string]>>,
  /**
   * The owner of a rig, if it is one of ours. Markets call this to admit miners.
   */
  'rig_owner' : ActorMethod<[Principal], [] | [Principal]>,
  'rigs_of' : ActorMethod<[Principal], Array<Principal>>,
  'search' : ActorMethod<[string], Array<Launch>>,
  /**
   * Stop (reason Some) or resume (None) featuring a token on Soon's website. Controllers only.
   */
  'set_hidden' : ActorMethod<[Principal, [] | [string]], Result>,
  /**
   * A token's creator can mark it NSFW or take back their own mark (if they ticked the box by
   * mistake). It can't undo a mark Soon made: that stays until a controller clears it.
   */
  'set_my_token_nsfw' : ActorMethod<[Principal, boolean], Result>,
  /**
   * Set (or, with an empty name, clear) your display name. One change every 10 minutes.
   */
  'set_name' : ActorMethod<[string], Result>,
  /**
   * Mark (true) or unmark (false) a token as NSFW. Controllers only. A creator's own NSFW mark stays.
   */
  'set_nsfw' : ActorMethod<[Principal, boolean], Result>,
  /**
   * Name one of your rigs (empty clears it). Only the rig's owner can.
   */
  'set_rig_name' : ActorMethod<[Principal, string], Result>,
  /**
   * Beta: which extra canisters (website, analytics) the factory keeps fuelled. Each must have the
   * blackhole as a controller so the factory can read its balance.
   */
  'set_upkeep' : ActorMethod<[Array<Principal>], undefined>,
  /**
   * Beta: let one upkeep canister receive more than the usual 2T a day (the social canister does
   * the most work). At most 10T a day; 0 goes back to the default.
   */
  'set_upkeep_cap' : ActorMethod<[Principal, bigint], undefined>,
  'spawn_rig' : ActorMethod<[SpawnRigArgs], Result_1>,
  /**
   * Start a rig fuelled with TCYCLES instead of ICP. Same rig, same fee; no CMC conversion.
   */
  'spawn_rig_with_cycles' : ActorMethod<[SpawnRigWithCyclesArgs], Result_1>,
  'stats' : ActorMethod<[], Stats>,
  /**
   * The graduation reserve only needs to cover a few listings at a time. Anyone can sweep ICP above
   * the cap to the treasury; it also runs daily.
   */
  'sweep_graduation_fund' : ActorMethod<[], Result_3>,
  /**
   * The TCYCLES side of the mining fee, and the limits for fuelling rigs with TCYCLES.
   */
  'tc_fees' : ActorMethod<[], TcFeesView>,
  /**
   * Convert ICP into cycles for any Soon canister: a rig, a market or a token ledger.
   */
  'top_up' : ActorMethod<[Principal, bigint], Result_3>,
  /**
   * Fuel a rig (or a market or token ledger) with TCYCLES instead of ICP. Rig fuel pays the 1%
   * mining fee; market and ledger fuel is free. Approve the factory for `cycles` plus 2 ledger fees
   * (1 for a market or ledger). Returns the cycles delivered.
   */
  'top_up_with_cycles' : ActorMethod<[Principal, bigint], Result_3>,
  /**
   * Beta only: apply the archive settings above to existing token ledgers (same ledger code, new
   * settings), one at a time with stop / upgrade / start like the markets. An empty list means every
   * token. Only the factory's own controllers can call it.
   */
  'upgrade_ledgers' : ActorMethod<
    [Array<Principal>],
    Array<[Principal, Result]>
  >,
  /**
   * Beta only: move existing markets onto the market code built into this factory, keeping their
   * state. Only the factory's own controllers can call it. Once the factory is handed to the
   * blackhole nobody can call it, and once markets are blackholed it has no power over them.
   */
  'upgrade_markets' : ActorMethod<
    [Array<Principal>],
    Array<[Principal, Result]>
  >,
  /**
   * Beta only: move rigs onto the rig code built into this factory, keeping their state. An empty
   * list means every rig. Only the factory's own controllers can call it.
   */
  'upgrade_rigs' : ActorMethod<[Array<Principal>], Array<[Principal, Result]>>,
  /**
   * Public: the upkeep canisters with a raised daily allowance.
   */
  'upkeep_caps' : ActorMethod<[], Array<[Principal, bigint]>>,
}
export const IcpSwapIds = IDL.Record({
  'passcode_manager' : IDL.Principal,
  'factory' : IDL.Principal,
  'calculator' : IDL.Principal,
});
export const BaseToken = IDL.Record({
  'fee' : IDL.Nat,
  'decimals' : IDL.Nat8,
  'icpswap_standard' : IDL.Text,
  'ledger' : IDL.Principal,
  'symbol' : IDL.Text,
});
export const BaseConfig = IDL.Record({
  'token' : BaseToken,
  'xrc_symbol' : IDL.Opt(IDL.Text),
  'price_cmc' : IDL.Opt(IDL.Bool),
  'price_pool' : IDL.Opt(IDL.Principal),
});
export const FactoryInit = IDL.Record({
  'cmc' : IDL.Opt(IDL.Principal),
  'xrc' : IDL.Opt(IDL.Principal),
  'graduation_fund_cap_e8s' : IDL.Nat64,
  'graduation_reserve_e8s' : IDL.Nat64,
  'icpswap' : IcpSwapIds,
  'market_cycles' : IDL.Nat,
  'icp_ledger' : IDL.Principal,
  'virtual_base_usd' : IDL.Float64,
  'child_controllers' : IDL.Vec(IDL.Principal),
  'rig_min_e8s' : IDL.Nat64,
  'bases' : IDL.Vec(BaseConfig),
  'ledger_cycles' : IDL.Nat,
  'dev_usd_prices' : IDL.Vec(IDL.Tuple(IDL.Principal, IDL.Float64)),
  'status_proxy' : IDL.Opt(IDL.Principal),
  'treasury' : IDL.Principal,
});
export const Result = IDL.Variant({ 'Ok' : IDL.Null, 'Err' : IDL.Text });
export const Bucket = IDL.Record({
  'cycles_burned' : IDL.Nat,
  'launches' : IDL.Nat64,
  'hour' : IDL.Nat64,
  'trades' : IDL.Nat64,
  'rigs' : IDL.Nat64,
  'volume' : IDL.Vec(IDL.Tuple(IDL.Principal, IDL.Nat)),
  'graduations' : IDL.Nat64,
  'fees_platform' : IDL.Vec(IDL.Tuple(IDL.Principal, IDL.Nat)),
  'blocks' : IDL.Nat64,
  'cycles_minted' : IDL.Nat,
  'fees_creator' : IDL.Vec(IDL.Tuple(IDL.Principal, IDL.Nat)),
});
export const Config = IDL.Record({
  'cmc' : IDL.Opt(IDL.Principal),
  'xrc' : IDL.Opt(IDL.Principal),
  'mine_supply' : IDL.Nat,
  'lp_supply' : IDL.Nat,
  'rig_tick_secs' : IDL.Nat64,
  'rig_hashes_per_power' : IDL.Nat64,
  'halving_blocks' : IDL.Nat64,
  'block_secs' : IDL.Nat64,
  'fee_bps' : IDL.Nat32,
  'icpswap' : IcpSwapIds,
  'rig_min_cycles' : IDL.Opt(IDL.Nat),
  'icp_ledger' : IDL.Principal,
  'virtual_base_usd' : IDL.Float64,
  'child_controllers' : IDL.Vec(IDL.Principal),
  'rig_min_e8s' : IDL.Nat64,
  'factory' : IDL.Principal,
  'bases' : IDL.Vec(BaseConfig),
  'curve_supply' : IDL.Nat,
  'ledger_wasm_sha256' : IDL.Text,
  'total_supply' : IDL.Nat,
  'cycles_ledger' : IDL.Opt(IDL.Principal),
  'treasury' : IDL.Principal,
});
export const EventKind = IDL.Variant({
  'Buy' : IDL.Record({ 'base' : IDL.Nat, 'tokens' : IDL.Nat }),
  'Launched' : IDL.Null,
  'Sell' : IDL.Record({ 'base' : IDL.Nat, 'tokens' : IDL.Nat }),
  'Block' : IDL.Record({ 'height' : IDL.Nat64, 'reward' : IDL.Nat }),
  'Graduated' : IDL.Record({ 'pool' : IDL.Principal }),
});
export const EventView = IDL.Record({
  'at' : IDL.Nat64,
  'who' : IDL.Principal,
  'base' : IDL.Opt(IDL.Principal),
  'kind' : EventKind,
  'name' : IDL.Text,
  'market' : IDL.Principal,
  'symbol' : IDL.Text,
});
export const Result_1 = IDL.Variant({ 'Ok' : IDL.Principal, 'Err' : IDL.Text });
export const Health = IDL.Record({
  'floor' : IDL.Nat,
  'last_error' : IDL.Opt(IDL.Text),
  'last_refuel_e8s' : IDL.Nat64,
  'factory_cycles' : IDL.Nat,
  'last_refuel_at' : IDL.Nat64,
  'canisters' : IDL.Vec(IDL.Tuple(IDL.Principal, IDL.Nat)),
  'pools' : IDL.Vec(IDL.Tuple(IDL.Principal, IDL.Principal, IDL.Nat)),
  'markets_low' : IDL.Nat32,
  'spendable_icp_e8s' : IDL.Nat64,
  'keep_for_listings_e8s' : IDL.Nat64,
  'last_run' : IDL.Nat64,
  'pool_fuel_e8s' : IDL.Nat64,
});
export const TokenMeta = IDL.Record({
  'x' : IDL.Opt(IDL.Text),
  'logo' : IDL.Text,
  'name' : IDL.Text,
  'description' : IDL.Text,
  'website' : IDL.Opt(IDL.Text),
  'telegram' : IDL.Opt(IDL.Text),
  'symbol' : IDL.Text,
});
export const LaunchArgs = IDL.Record({
  'base' : IDL.Principal,
  'meta' : TokenMeta,
  'nsfw' : IDL.Opt(IDL.Bool),
  'dev_buy' : IDL.Opt(IDL.Nat),
});
export const JobStage = IDL.Variant({
  'MarketInstalled' : IDL.Record({
    'ledger' : IDL.Principal,
    'market' : IDL.Principal,
  }),
  'LedgerInstalled' : IDL.Record({
    'ledger' : IDL.Principal,
    'market' : IDL.Principal,
  }),
  'Done' : IDL.Record({ 'ledger' : IDL.Principal, 'market' : IDL.Principal }),
  'Paid' : IDL.Null,
  'BothCreated' : IDL.Record({
    'ledger' : IDL.Principal,
    'market' : IDL.Principal,
  }),
  'Funded' : IDL.Null,
  'MarketCreated' : IDL.Record({ 'market' : IDL.Principal }),
});
export const Job = IDL.Record({
  'id' : IDL.Nat64,
  'x0' : IDL.Nat,
  'creator' : IDL.Principal,
  'args' : LaunchArgs,
  'fee_e8s' : IDL.Nat64,
  'created_at' : IDL.Nat64,
  'error' : IDL.Opt(IDL.Text),
  'stage' : JobStage,
  'dev_buy' : IDL.Opt(IDL.Nat),
  'dev_sent' : IDL.Opt(IDL.Bool),
});
export const Result_2 = IDL.Variant({ 'Ok' : Job, 'Err' : IDL.Text });
export const Launch = IDL.Record({
  'mined' : IDL.Nat,
  'creator' : IDL.Principal,
  'base' : IDL.Principal,
  'trades' : IDL.Nat64,
  'name' : IDL.Text,
  'nsfw' : IDL.Opt(IDL.Bool),
  'sold' : IDL.Nat,
  'description' : IDL.Text,
  'created_at' : IDL.Nat64,
  'price_x' : IDL.Nat,
  'price_y' : IDL.Nat,
  'ledger' : IDL.Principal,
  'reported_at' : IDL.Nat64,
  'blocks' : IDL.Nat64,
  'last_trade_at' : IDL.Nat64,
  'graduated' : IDL.Bool,
  'dev_tokens' : IDL.Opt(IDL.Nat),
  'raised' : IDL.Nat,
  'progress_bps' : IDL.Nat32,
  'market' : IDL.Principal,
  'active_rigs' : IDL.Nat32,
  'icpswap_pool' : IDL.Opt(IDL.Principal),
  'symbol' : IDL.Text,
  'volume_base' : IDL.Nat,
});
export const Sort = IDL.Variant({
  'Active' : IDL.Null,
  'Graduated' : IDL.Null,
  'Progress' : IDL.Null,
  'Miners' : IDL.Null,
  'Newest' : IDL.Null,
  'Raised' : IDL.Null,
});
export const Page = IDL.Record({
  'total' : IDL.Nat64,
  'items' : IDL.Vec(Launch),
});
export const MiningFeesView = IDL.Record({
  'platform_total_e8s' : IDL.Nat,
  'creator_total_e8s' : IDL.Nat,
  'fee_bps' : IDL.Nat32,
  'creator_owed_e8s' : IDL.Nat,
  'creator_paid_e8s' : IDL.Nat,
});
export const Result_3 = IDL.Variant({ 'Ok' : IDL.Nat, 'Err' : IDL.Text });
export const UsdPrice = IDL.Record({
  'at' : IDL.Nat64,
  'usd' : IDL.Float64,
  'ledger' : IDL.Principal,
  'symbol' : IDL.Text,
});
export const LaunchQuote = IDL.Record({
  'x0' : IDL.Nat,
  'usd_price' : IDL.Float64,
  'base' : BaseToken,
  'fee_e8s' : IDL.Nat64,
  'graduation_raise' : IDL.Nat,
});
export const Result_4 = IDL.Variant({ 'Ok' : LaunchQuote, 'Err' : IDL.Text });
export const Event = IDL.Record({
  'at' : IDL.Nat64,
  'who' : IDL.Principal,
  'kind' : EventKind,
  'market' : IDL.Principal,
});
export const MarketReport = IDL.Record({
  'mined' : IDL.Nat,
  'buys' : IDL.Nat64,
  'trades' : IDL.Nat64,
  'sold' : IDL.Nat,
  'price_x' : IDL.Nat,
  'price_y' : IDL.Nat,
  'cycles' : IDL.Nat,
  'cycles_burned_rigs' : IDL.Nat,
  'cycles_burned_market' : IDL.Nat,
  'events' : IDL.Vec(Event),
  'fees_platform' : IDL.Nat,
  'blocks' : IDL.Nat64,
  'graduated' : IDL.Bool,
  'raised' : IDL.Nat,
  'active_rigs' : IDL.Nat32,
  'icpswap_pool' : IDL.Opt(IDL.Principal),
  'volume_base' : IDL.Nat,
  'fees_creator' : IDL.Nat,
});
export const Result_5 = IDL.Variant({ 'Ok' : IDL.Nat64, 'Err' : IDL.Text });
export const TokenReport = IDL.Record({
  'at' : IDL.Nat64,
  'id' : IDL.Nat64,
  'contact' : IDL.Opt(IDL.Text),
  'details' : IDL.Text,
  'market' : IDL.Principal,
  'reporter' : IDL.Principal,
  'reason' : IDL.Text,
});
export const SpawnRigArgs = IDL.Record({
  'e8s' : IDL.Nat64,
  'market' : IDL.Opt(IDL.Principal),
  'power' : IDL.Nat8,
});
export const SpawnRigWithCyclesArgs = IDL.Record({
  'cycles' : IDL.Nat,
  'market' : IDL.Opt(IDL.Principal),
  'power' : IDL.Nat8,
});
export const Stats = IDL.Record({
  'now' : IDL.Nat64,
  'factory_cycles' : IDL.Nat,
  'launches' : IDL.Nat64,
  'trades' : IDL.Nat64,
  'rigs' : IDL.Nat64,
  'volume' : IDL.Vec(IDL.Tuple(IDL.Principal, IDL.Nat)),
  'icp_converted_e8s' : IDL.Nat,
  'graduation_fund_e8s' : IDL.Nat64,
  'cycles_burned_rigs' : IDL.Nat,
  'graduations' : IDL.Nat64,
  'cycles_spent_creating' : IDL.Nat,
  'fees_platform' : IDL.Vec(IDL.Tuple(IDL.Principal, IDL.Nat)),
  'cycles_burned_total' : IDL.Nat,
  'blocks' : IDL.Nat64,
  'cycles_minted' : IDL.Nat,
  'cycles_burned_markets' : IDL.Nat,
  'cycles_burned_factory' : IDL.Nat,
  'fees_creator' : IDL.Vec(IDL.Tuple(IDL.Principal, IDL.Nat)),
});
export const TcUncertain = IDL.Record({
  'to' : IDL.Principal,
  'fee' : IDL.Nat,
  'who' : IDL.Principal,
  'fuel' : IDL.Nat,
  'created_at' : IDL.Nat64,
  'market' : IDL.Opt(IDL.Principal),
  'reason' : IDL.Text,
});
export const TcFeesView = IDL.Record({
  'platform_paid_cycles' : IDL.Nat,
  'refunds_owed_cycles' : IDL.Nat,
  'held_for_others_cycles' : IDL.Nat,
  'fee_bps' : IDL.Nat32,
  'uncertain' : IDL.Vec(IDL.Tuple(IDL.Nat64, TcUncertain)),
  'creator_owed_cycles' : IDL.Nat,
  'rig_min_cycles' : IDL.Nat,
  'top_up_min_cycles' : IDL.Nat,
  'delivered_cycles' : IDL.Nat,
  'platform_total_cycles' : IDL.Nat,
  'creator_paid_cycles' : IDL.Nat,
  'creator_total_cycles' : IDL.Nat,
  'cycles_ledger' : IDL.Opt(IDL.Principal),
});

export const idlService: IDL.ServiceClass = IDL.Service({
  'add_base' : IDL.Func([BaseConfig], [Result], []),
  'buckets' : IDL.Func([IDL.Nat64, IDL.Nat64], [IDL.Vec(Bucket)], ['query']),
  'clear_name' : IDL.Func([IDL.Principal], [Result], []),
  'config' : IDL.Func([], [Config], ['query']),
  'events' : IDL.Func([IDL.Nat32], [IDL.Vec(EventView)], ['query']),
  'graduate' : IDL.Func([IDL.Principal], [Result_1], []),
  'health' : IDL.Func([], [Health], ['query']),
  'hidden' : IDL.Func(
      [],
      [IDL.Vec(IDL.Tuple(IDL.Principal, IDL.Text, IDL.Nat64))],
      ['query'],
    ),
  'job' : IDL.Func([IDL.Nat64], [IDL.Opt(Job)], ['query']),
  'jobs_of' : IDL.Func([IDL.Principal], [IDL.Vec(Job)], ['query']),
  'launch' : IDL.Func([LaunchArgs], [Result_2], []),
  'launch_of' : IDL.Func([IDL.Principal], [IDL.Opt(Launch)], ['query']),
  'market_of_ledger' : IDL.Func(
      [IDL.Principal],
      [IDL.Opt(IDL.Principal)],
      ['query'],
    ),
  'markets' : IDL.Func(
      [Sort, IDL.Nat64, IDL.Nat64, IDL.Opt(IDL.Principal)],
      [Page],
      ['query'],
    ),
  'markets_of' : IDL.Func([IDL.Principal], [IDL.Vec(Launch)], ['query']),
  'mining_fee_owed' : IDL.Func([IDL.Principal], [IDL.Nat], ['query']),
  'mining_fee_owed_cycles' : IDL.Func([IDL.Principal], [IDL.Nat], ['query']),
  'mining_fees' : IDL.Func([], [MiningFeesView], ['query']),
  'names' : IDL.Func(
      [IDL.Vec(IDL.Principal)],
      [IDL.Vec(IDL.Tuple(IDL.Principal, IDL.Text))],
      ['query'],
    ),
  'nsfw' : IDL.Func([], [IDL.Vec(IDL.Principal)], ['query']),
  'pay_mining_fees' : IDL.Func([], [Result_3], []),
  'pay_tc_fees' : IDL.Func([], [Result_3], []),
  'prices' : IDL.Func([], [IDL.Vec(UsdPrice)], ['query']),
  'quote_launch' : IDL.Func([IDL.Principal], [Result_4], ['query']),
  'report' : IDL.Func([MarketReport], [], []),
  'report_token' : IDL.Func(
      [IDL.Principal, IDL.Text, IDL.Text, IDL.Opt(IDL.Text)],
      [Result_5],
      [],
    ),
  'reports' : IDL.Func(
      [IDL.Nat64, IDL.Nat64],
      [IDL.Vec(TokenReport)],
      ['query'],
    ),
  'resolve_names' : IDL.Func(
      [IDL.Vec(IDL.Text)],
      [IDL.Vec(IDL.Tuple(IDL.Text, IDL.Principal))],
      ['query'],
    ),
  'resolve_tc_uncertain' : IDL.Func([IDL.Nat64, IDL.Bool], [Result], []),
  'resume_launch' : IDL.Func([IDL.Nat64], [Result_2], []),
  'resume_rig' : IDL.Func([IDL.Principal], [Result_1], []),
  'rig_names' : IDL.Func(
      [IDL.Vec(IDL.Principal)],
      [IDL.Vec(IDL.Tuple(IDL.Principal, IDL.Text))],
      ['query'],
    ),
  'rig_owner' : IDL.Func([IDL.Principal], [IDL.Opt(IDL.Principal)], ['query']),
  'rigs_of' : IDL.Func([IDL.Principal], [IDL.Vec(IDL.Principal)], ['query']),
  'search' : IDL.Func([IDL.Text], [IDL.Vec(Launch)], ['query']),
  'set_hidden' : IDL.Func([IDL.Principal, IDL.Opt(IDL.Text)], [Result], []),
  'set_my_token_nsfw' : IDL.Func([IDL.Principal, IDL.Bool], [Result], []),
  'set_name' : IDL.Func([IDL.Text], [Result], []),
  'set_nsfw' : IDL.Func([IDL.Principal, IDL.Bool], [Result], []),
  'set_rig_name' : IDL.Func([IDL.Principal, IDL.Text], [Result], []),
  'set_upkeep' : IDL.Func([IDL.Vec(IDL.Principal)], [], []),
  'set_upkeep_cap' : IDL.Func([IDL.Principal, IDL.Nat], [], []),
  'spawn_rig' : IDL.Func([SpawnRigArgs], [Result_1], []),
  'spawn_rig_with_cycles' : IDL.Func([SpawnRigWithCyclesArgs], [Result_1], []),
  'stats' : IDL.Func([], [Stats], ['query']),
  'sweep_graduation_fund' : IDL.Func([], [Result_3], []),
  'tc_fees' : IDL.Func([], [TcFeesView], ['query']),
  'top_up' : IDL.Func([IDL.Principal, IDL.Nat64], [Result_3], []),
  'top_up_with_cycles' : IDL.Func([IDL.Principal, IDL.Nat], [Result_3], []),
  'upgrade_ledgers' : IDL.Func(
      [IDL.Vec(IDL.Principal)],
      [IDL.Vec(IDL.Tuple(IDL.Principal, Result))],
      [],
    ),
  'upgrade_markets' : IDL.Func(
      [IDL.Vec(IDL.Principal)],
      [IDL.Vec(IDL.Tuple(IDL.Principal, Result))],
      [],
    ),
  'upgrade_rigs' : IDL.Func(
      [IDL.Vec(IDL.Principal)],
      [IDL.Vec(IDL.Tuple(IDL.Principal, Result))],
      [],
    ),
  'upkeep_caps' : IDL.Func(
      [],
      [IDL.Vec(IDL.Tuple(IDL.Principal, IDL.Nat))],
      ['query'],
    ),
});

export const idlInitArgs: IDL.Type[] = [FactoryInit];

export const idlFactory: IDL.InterfaceFactory = ({ IDL }) => {
  const IcpSwapIds = IDL.Record({
    'passcode_manager' : IDL.Principal,
    'factory' : IDL.Principal,
    'calculator' : IDL.Principal,
  });
  const BaseToken = IDL.Record({
    'fee' : IDL.Nat,
    'decimals' : IDL.Nat8,
    'icpswap_standard' : IDL.Text,
    'ledger' : IDL.Principal,
    'symbol' : IDL.Text,
  });
  const BaseConfig = IDL.Record({
    'token' : BaseToken,
    'xrc_symbol' : IDL.Opt(IDL.Text),
    'price_cmc' : IDL.Opt(IDL.Bool),
    'price_pool' : IDL.Opt(IDL.Principal),
  });
  const FactoryInit = IDL.Record({
    'cmc' : IDL.Opt(IDL.Principal),
    'xrc' : IDL.Opt(IDL.Principal),
    'graduation_fund_cap_e8s' : IDL.Nat64,
    'graduation_reserve_e8s' : IDL.Nat64,
    'icpswap' : IcpSwapIds,
    'market_cycles' : IDL.Nat,
    'icp_ledger' : IDL.Principal,
    'virtual_base_usd' : IDL.Float64,
    'child_controllers' : IDL.Vec(IDL.Principal),
    'rig_min_e8s' : IDL.Nat64,
    'bases' : IDL.Vec(BaseConfig),
    'ledger_cycles' : IDL.Nat,
    'dev_usd_prices' : IDL.Vec(IDL.Tuple(IDL.Principal, IDL.Float64)),
    'status_proxy' : IDL.Opt(IDL.Principal),
    'treasury' : IDL.Principal,
  });
  const Result = IDL.Variant({ 'Ok' : IDL.Null, 'Err' : IDL.Text });
  const Bucket = IDL.Record({
    'cycles_burned' : IDL.Nat,
    'launches' : IDL.Nat64,
    'hour' : IDL.Nat64,
    'trades' : IDL.Nat64,
    'rigs' : IDL.Nat64,
    'volume' : IDL.Vec(IDL.Tuple(IDL.Principal, IDL.Nat)),
    'graduations' : IDL.Nat64,
    'fees_platform' : IDL.Vec(IDL.Tuple(IDL.Principal, IDL.Nat)),
    'blocks' : IDL.Nat64,
    'cycles_minted' : IDL.Nat,
    'fees_creator' : IDL.Vec(IDL.Tuple(IDL.Principal, IDL.Nat)),
  });
  const Config = IDL.Record({
    'cmc' : IDL.Opt(IDL.Principal),
    'xrc' : IDL.Opt(IDL.Principal),
    'mine_supply' : IDL.Nat,
    'lp_supply' : IDL.Nat,
    'rig_tick_secs' : IDL.Nat64,
    'rig_hashes_per_power' : IDL.Nat64,
    'halving_blocks' : IDL.Nat64,
    'block_secs' : IDL.Nat64,
    'fee_bps' : IDL.Nat32,
    'icpswap' : IcpSwapIds,
    'rig_min_cycles' : IDL.Opt(IDL.Nat),
    'icp_ledger' : IDL.Principal,
    'virtual_base_usd' : IDL.Float64,
    'child_controllers' : IDL.Vec(IDL.Principal),
    'rig_min_e8s' : IDL.Nat64,
    'factory' : IDL.Principal,
    'bases' : IDL.Vec(BaseConfig),
    'curve_supply' : IDL.Nat,
    'ledger_wasm_sha256' : IDL.Text,
    'total_supply' : IDL.Nat,
    'cycles_ledger' : IDL.Opt(IDL.Principal),
    'treasury' : IDL.Principal,
  });
  const EventKind = IDL.Variant({
    'Buy' : IDL.Record({ 'base' : IDL.Nat, 'tokens' : IDL.Nat }),
    'Launched' : IDL.Null,
    'Sell' : IDL.Record({ 'base' : IDL.Nat, 'tokens' : IDL.Nat }),
    'Block' : IDL.Record({ 'height' : IDL.Nat64, 'reward' : IDL.Nat }),
    'Graduated' : IDL.Record({ 'pool' : IDL.Principal }),
  });
  const EventView = IDL.Record({
    'at' : IDL.Nat64,
    'who' : IDL.Principal,
    'base' : IDL.Opt(IDL.Principal),
    'kind' : EventKind,
    'name' : IDL.Text,
    'market' : IDL.Principal,
    'symbol' : IDL.Text,
  });
  const Result_1 = IDL.Variant({ 'Ok' : IDL.Principal, 'Err' : IDL.Text });
  const Health = IDL.Record({
    'floor' : IDL.Nat,
    'last_error' : IDL.Opt(IDL.Text),
    'last_refuel_e8s' : IDL.Nat64,
    'factory_cycles' : IDL.Nat,
    'last_refuel_at' : IDL.Nat64,
    'canisters' : IDL.Vec(IDL.Tuple(IDL.Principal, IDL.Nat)),
    'pools' : IDL.Vec(IDL.Tuple(IDL.Principal, IDL.Principal, IDL.Nat)),
    'markets_low' : IDL.Nat32,
    'spendable_icp_e8s' : IDL.Nat64,
    'keep_for_listings_e8s' : IDL.Nat64,
    'last_run' : IDL.Nat64,
    'pool_fuel_e8s' : IDL.Nat64,
  });
  const TokenMeta = IDL.Record({
    'x' : IDL.Opt(IDL.Text),
    'logo' : IDL.Text,
    'name' : IDL.Text,
    'description' : IDL.Text,
    'website' : IDL.Opt(IDL.Text),
    'telegram' : IDL.Opt(IDL.Text),
    'symbol' : IDL.Text,
  });
  const LaunchArgs = IDL.Record({
    'base' : IDL.Principal,
    'meta' : TokenMeta,
    'nsfw' : IDL.Opt(IDL.Bool),
    'dev_buy' : IDL.Opt(IDL.Nat),
  });
  const JobStage = IDL.Variant({
    'MarketInstalled' : IDL.Record({
      'ledger' : IDL.Principal,
      'market' : IDL.Principal,
    }),
    'LedgerInstalled' : IDL.Record({
      'ledger' : IDL.Principal,
      'market' : IDL.Principal,
    }),
    'Done' : IDL.Record({ 'ledger' : IDL.Principal, 'market' : IDL.Principal }),
    'Paid' : IDL.Null,
    'BothCreated' : IDL.Record({
      'ledger' : IDL.Principal,
      'market' : IDL.Principal,
    }),
    'Funded' : IDL.Null,
    'MarketCreated' : IDL.Record({ 'market' : IDL.Principal }),
  });
  const Job = IDL.Record({
    'id' : IDL.Nat64,
    'x0' : IDL.Nat,
    'creator' : IDL.Principal,
    'args' : LaunchArgs,
    'fee_e8s' : IDL.Nat64,
    'created_at' : IDL.Nat64,
    'error' : IDL.Opt(IDL.Text),
    'stage' : JobStage,
    'dev_buy' : IDL.Opt(IDL.Nat),
    'dev_sent' : IDL.Opt(IDL.Bool),
  });
  const Result_2 = IDL.Variant({ 'Ok' : Job, 'Err' : IDL.Text });
  const Launch = IDL.Record({
    'mined' : IDL.Nat,
    'creator' : IDL.Principal,
    'base' : IDL.Principal,
    'trades' : IDL.Nat64,
    'name' : IDL.Text,
    'nsfw' : IDL.Opt(IDL.Bool),
    'sold' : IDL.Nat,
    'description' : IDL.Text,
    'created_at' : IDL.Nat64,
    'price_x' : IDL.Nat,
    'price_y' : IDL.Nat,
    'ledger' : IDL.Principal,
    'reported_at' : IDL.Nat64,
    'blocks' : IDL.Nat64,
    'last_trade_at' : IDL.Nat64,
    'graduated' : IDL.Bool,
    'dev_tokens' : IDL.Opt(IDL.Nat),
    'raised' : IDL.Nat,
    'progress_bps' : IDL.Nat32,
    'market' : IDL.Principal,
    'active_rigs' : IDL.Nat32,
    'icpswap_pool' : IDL.Opt(IDL.Principal),
    'symbol' : IDL.Text,
    'volume_base' : IDL.Nat,
  });
  const Sort = IDL.Variant({
    'Active' : IDL.Null,
    'Graduated' : IDL.Null,
    'Progress' : IDL.Null,
    'Miners' : IDL.Null,
    'Newest' : IDL.Null,
    'Raised' : IDL.Null,
  });
  const Page = IDL.Record({ 'total' : IDL.Nat64, 'items' : IDL.Vec(Launch) });
  const MiningFeesView = IDL.Record({
    'platform_total_e8s' : IDL.Nat,
    'creator_total_e8s' : IDL.Nat,
    'fee_bps' : IDL.Nat32,
    'creator_owed_e8s' : IDL.Nat,
    'creator_paid_e8s' : IDL.Nat,
  });
  const Result_3 = IDL.Variant({ 'Ok' : IDL.Nat, 'Err' : IDL.Text });
  const UsdPrice = IDL.Record({
    'at' : IDL.Nat64,
    'usd' : IDL.Float64,
    'ledger' : IDL.Principal,
    'symbol' : IDL.Text,
  });
  const LaunchQuote = IDL.Record({
    'x0' : IDL.Nat,
    'usd_price' : IDL.Float64,
    'base' : BaseToken,
    'fee_e8s' : IDL.Nat64,
    'graduation_raise' : IDL.Nat,
  });
  const Result_4 = IDL.Variant({ 'Ok' : LaunchQuote, 'Err' : IDL.Text });
  const Event = IDL.Record({
    'at' : IDL.Nat64,
    'who' : IDL.Principal,
    'kind' : EventKind,
    'market' : IDL.Principal,
  });
  const MarketReport = IDL.Record({
    'mined' : IDL.Nat,
    'buys' : IDL.Nat64,
    'trades' : IDL.Nat64,
    'sold' : IDL.Nat,
    'price_x' : IDL.Nat,
    'price_y' : IDL.Nat,
    'cycles' : IDL.Nat,
    'cycles_burned_rigs' : IDL.Nat,
    'cycles_burned_market' : IDL.Nat,
    'events' : IDL.Vec(Event),
    'fees_platform' : IDL.Nat,
    'blocks' : IDL.Nat64,
    'graduated' : IDL.Bool,
    'raised' : IDL.Nat,
    'active_rigs' : IDL.Nat32,
    'icpswap_pool' : IDL.Opt(IDL.Principal),
    'volume_base' : IDL.Nat,
    'fees_creator' : IDL.Nat,
  });
  const Result_5 = IDL.Variant({ 'Ok' : IDL.Nat64, 'Err' : IDL.Text });
  const TokenReport = IDL.Record({
    'at' : IDL.Nat64,
    'id' : IDL.Nat64,
    'contact' : IDL.Opt(IDL.Text),
    'details' : IDL.Text,
    'market' : IDL.Principal,
    'reporter' : IDL.Principal,
    'reason' : IDL.Text,
  });
  const SpawnRigArgs = IDL.Record({
    'e8s' : IDL.Nat64,
    'market' : IDL.Opt(IDL.Principal),
    'power' : IDL.Nat8,
  });
  const SpawnRigWithCyclesArgs = IDL.Record({
    'cycles' : IDL.Nat,
    'market' : IDL.Opt(IDL.Principal),
    'power' : IDL.Nat8,
  });
  const Stats = IDL.Record({
    'now' : IDL.Nat64,
    'factory_cycles' : IDL.Nat,
    'launches' : IDL.Nat64,
    'trades' : IDL.Nat64,
    'rigs' : IDL.Nat64,
    'volume' : IDL.Vec(IDL.Tuple(IDL.Principal, IDL.Nat)),
    'icp_converted_e8s' : IDL.Nat,
    'graduation_fund_e8s' : IDL.Nat64,
    'cycles_burned_rigs' : IDL.Nat,
    'graduations' : IDL.Nat64,
    'cycles_spent_creating' : IDL.Nat,
    'fees_platform' : IDL.Vec(IDL.Tuple(IDL.Principal, IDL.Nat)),
    'cycles_burned_total' : IDL.Nat,
    'blocks' : IDL.Nat64,
    'cycles_minted' : IDL.Nat,
    'cycles_burned_markets' : IDL.Nat,
    'cycles_burned_factory' : IDL.Nat,
    'fees_creator' : IDL.Vec(IDL.Tuple(IDL.Principal, IDL.Nat)),
  });
  const TcUncertain = IDL.Record({
    'to' : IDL.Principal,
    'fee' : IDL.Nat,
    'who' : IDL.Principal,
    'fuel' : IDL.Nat,
    'created_at' : IDL.Nat64,
    'market' : IDL.Opt(IDL.Principal),
    'reason' : IDL.Text,
  });
  const TcFeesView = IDL.Record({
    'platform_paid_cycles' : IDL.Nat,
    'refunds_owed_cycles' : IDL.Nat,
    'held_for_others_cycles' : IDL.Nat,
    'fee_bps' : IDL.Nat32,
    'uncertain' : IDL.Vec(IDL.Tuple(IDL.Nat64, TcUncertain)),
    'creator_owed_cycles' : IDL.Nat,
    'rig_min_cycles' : IDL.Nat,
    'top_up_min_cycles' : IDL.Nat,
    'delivered_cycles' : IDL.Nat,
    'platform_total_cycles' : IDL.Nat,
    'creator_paid_cycles' : IDL.Nat,
    'creator_total_cycles' : IDL.Nat,
    'cycles_ledger' : IDL.Opt(IDL.Principal),
  });

  return IDL.Service({
    'add_base' : IDL.Func([BaseConfig], [Result], []),
    'buckets' : IDL.Func([IDL.Nat64, IDL.Nat64], [IDL.Vec(Bucket)], ['query']),
    'clear_name' : IDL.Func([IDL.Principal], [Result], []),
    'config' : IDL.Func([], [Config], ['query']),
    'events' : IDL.Func([IDL.Nat32], [IDL.Vec(EventView)], ['query']),
    'graduate' : IDL.Func([IDL.Principal], [Result_1], []),
    'health' : IDL.Func([], [Health], ['query']),
    'hidden' : IDL.Func(
        [],
        [IDL.Vec(IDL.Tuple(IDL.Principal, IDL.Text, IDL.Nat64))],
        ['query'],
      ),
    'job' : IDL.Func([IDL.Nat64], [IDL.Opt(Job)], ['query']),
    'jobs_of' : IDL.Func([IDL.Principal], [IDL.Vec(Job)], ['query']),
    'launch' : IDL.Func([LaunchArgs], [Result_2], []),
    'launch_of' : IDL.Func([IDL.Principal], [IDL.Opt(Launch)], ['query']),
    'market_of_ledger' : IDL.Func(
        [IDL.Principal],
        [IDL.Opt(IDL.Principal)],
        ['query'],
      ),
    'markets' : IDL.Func(
        [Sort, IDL.Nat64, IDL.Nat64, IDL.Opt(IDL.Principal)],
        [Page],
        ['query'],
      ),
    'markets_of' : IDL.Func([IDL.Principal], [IDL.Vec(Launch)], ['query']),
    'mining_fee_owed' : IDL.Func([IDL.Principal], [IDL.Nat], ['query']),
    'mining_fee_owed_cycles' : IDL.Func([IDL.Principal], [IDL.Nat], ['query']),
    'mining_fees' : IDL.Func([], [MiningFeesView], ['query']),
    'names' : IDL.Func(
        [IDL.Vec(IDL.Principal)],
        [IDL.Vec(IDL.Tuple(IDL.Principal, IDL.Text))],
        ['query'],
      ),
    'nsfw' : IDL.Func([], [IDL.Vec(IDL.Principal)], ['query']),
    'pay_mining_fees' : IDL.Func([], [Result_3], []),
    'pay_tc_fees' : IDL.Func([], [Result_3], []),
    'prices' : IDL.Func([], [IDL.Vec(UsdPrice)], ['query']),
    'quote_launch' : IDL.Func([IDL.Principal], [Result_4], ['query']),
    'report' : IDL.Func([MarketReport], [], []),
    'report_token' : IDL.Func(
        [IDL.Principal, IDL.Text, IDL.Text, IDL.Opt(IDL.Text)],
        [Result_5],
        [],
      ),
    'reports' : IDL.Func(
        [IDL.Nat64, IDL.Nat64],
        [IDL.Vec(TokenReport)],
        ['query'],
      ),
    'resolve_names' : IDL.Func(
        [IDL.Vec(IDL.Text)],
        [IDL.Vec(IDL.Tuple(IDL.Text, IDL.Principal))],
        ['query'],
      ),
    'resolve_tc_uncertain' : IDL.Func([IDL.Nat64, IDL.Bool], [Result], []),
    'resume_launch' : IDL.Func([IDL.Nat64], [Result_2], []),
    'resume_rig' : IDL.Func([IDL.Principal], [Result_1], []),
    'rig_names' : IDL.Func(
        [IDL.Vec(IDL.Principal)],
        [IDL.Vec(IDL.Tuple(IDL.Principal, IDL.Text))],
        ['query'],
      ),
    'rig_owner' : IDL.Func(
        [IDL.Principal],
        [IDL.Opt(IDL.Principal)],
        ['query'],
      ),
    'rigs_of' : IDL.Func([IDL.Principal], [IDL.Vec(IDL.Principal)], ['query']),
    'search' : IDL.Func([IDL.Text], [IDL.Vec(Launch)], ['query']),
    'set_hidden' : IDL.Func([IDL.Principal, IDL.Opt(IDL.Text)], [Result], []),
    'set_my_token_nsfw' : IDL.Func([IDL.Principal, IDL.Bool], [Result], []),
    'set_name' : IDL.Func([IDL.Text], [Result], []),
    'set_nsfw' : IDL.Func([IDL.Principal, IDL.Bool], [Result], []),
    'set_rig_name' : IDL.Func([IDL.Principal, IDL.Text], [Result], []),
    'set_upkeep' : IDL.Func([IDL.Vec(IDL.Principal)], [], []),
    'set_upkeep_cap' : IDL.Func([IDL.Principal, IDL.Nat], [], []),
    'spawn_rig' : IDL.Func([SpawnRigArgs], [Result_1], []),
    'spawn_rig_with_cycles' : IDL.Func(
        [SpawnRigWithCyclesArgs],
        [Result_1],
        [],
      ),
    'stats' : IDL.Func([], [Stats], ['query']),
    'sweep_graduation_fund' : IDL.Func([], [Result_3], []),
    'tc_fees' : IDL.Func([], [TcFeesView], ['query']),
    'top_up' : IDL.Func([IDL.Principal, IDL.Nat64], [Result_3], []),
    'top_up_with_cycles' : IDL.Func([IDL.Principal, IDL.Nat], [Result_3], []),
    'upgrade_ledgers' : IDL.Func(
        [IDL.Vec(IDL.Principal)],
        [IDL.Vec(IDL.Tuple(IDL.Principal, Result))],
        [],
      ),
    'upgrade_markets' : IDL.Func(
        [IDL.Vec(IDL.Principal)],
        [IDL.Vec(IDL.Tuple(IDL.Principal, Result))],
        [],
      ),
    'upgrade_rigs' : IDL.Func(
        [IDL.Vec(IDL.Principal)],
        [IDL.Vec(IDL.Tuple(IDL.Principal, Result))],
        [],
      ),
    'upkeep_caps' : IDL.Func(
        [],
        [IDL.Vec(IDL.Tuple(IDL.Principal, IDL.Nat))],
        ['query'],
      ),
  });
};

export const init: (args: { IDL: typeof IDL }) => IDL.Type[] = ({ IDL }) => {
  const IcpSwapIds = IDL.Record({
    'passcode_manager' : IDL.Principal,
    'factory' : IDL.Principal,
    'calculator' : IDL.Principal,
  });
  const BaseToken = IDL.Record({
    'fee' : IDL.Nat,
    'decimals' : IDL.Nat8,
    'icpswap_standard' : IDL.Text,
    'ledger' : IDL.Principal,
    'symbol' : IDL.Text,
  });
  const BaseConfig = IDL.Record({
    'token' : BaseToken,
    'xrc_symbol' : IDL.Opt(IDL.Text),
    'price_cmc' : IDL.Opt(IDL.Bool),
    'price_pool' : IDL.Opt(IDL.Principal),
  });
  const FactoryInit = IDL.Record({
    'cmc' : IDL.Opt(IDL.Principal),
    'xrc' : IDL.Opt(IDL.Principal),
    'graduation_fund_cap_e8s' : IDL.Nat64,
    'graduation_reserve_e8s' : IDL.Nat64,
    'icpswap' : IcpSwapIds,
    'market_cycles' : IDL.Nat,
    'icp_ledger' : IDL.Principal,
    'virtual_base_usd' : IDL.Float64,
    'child_controllers' : IDL.Vec(IDL.Principal),
    'rig_min_e8s' : IDL.Nat64,
    'bases' : IDL.Vec(BaseConfig),
    'ledger_cycles' : IDL.Nat,
    'dev_usd_prices' : IDL.Vec(IDL.Tuple(IDL.Principal, IDL.Float64)),
    'status_proxy' : IDL.Opt(IDL.Principal),
    'treasury' : IDL.Principal,
  });

  return [FactoryInit];
};
